import { useMemo } from 'react';
import map from 'lodash/map';
import flatMap from 'lodash/flatMap';

import {
  ProformaInvoiceFormGroupByMode,
  ProformaInvoiceFormViewMode
} from '../../../../../teams/components/forms/ProformaInvoiceForm/ProformaInvoiceForm.types';

import { InvoiceItemsViewItem } from '../useInvoiceItemsViewContext';

import { getInvoiceItemsViewGroupKey } from '../../utils/getInvoiceItemsViewGroupKey';

interface InvoiceItemsViewPreparedItemsOptions {
  items: InvoiceItemsViewItem[];
  viewMode: ProformaInvoiceFormViewMode;
  groupBy: ProformaInvoiceFormGroupByMode;
  enableGrouping: boolean;
}

function getViewModeKey(
  item: InvoiceItemsViewItem,
  viewMode: ProformaInvoiceFormViewMode
) {
  switch (viewMode) {
    case ProformaInvoiceFormViewMode.INVOICES:
      return `${'itemName' in item ? item.itemName : item.itemTypeName}-${
        item.convertedPricePerItem
      }`;

    case ProformaInvoiceFormViewMode.TASKS:
      return item.taskNanoId;

    case ProformaInvoiceFormViewMode.GROUPS:
      return item.projectNanoId;

    case ProformaInvoiceFormViewMode.PROJECTS:
      return 'rootProjectNanoId' in item
        ? item?.rootProjectNanoId
        : item.projectNanoId;

    case ProformaInvoiceFormViewMode.CATEGORIES:
      return item.itemCategoryName;

    default:
      return '';
  }
}

function useInvoiceItemsViewPreparedItems({
  items,
  viewMode,
  groupBy,
  enableGrouping
}: InvoiceItemsViewPreparedItemsOptions) {
  const mergedItems = useMemo(() => {
    const mergedItemsBySplitKey: Record<string, InvoiceItemsViewItem> = {};

    return items.reduce<InvoiceItemsViewItem[]>((acc, groupedItem) => {
      if (!groupedItem.splitPartPercent) {
        acc.push(groupedItem);
        return acc;
      }

      const splitKey = [
        getInvoiceItemsViewGroupKey({
          item: groupedItem,
          enableGrouping,
          groupBy
        }),
        getViewModeKey(groupedItem, viewMode),
        groupedItem.splitPartPercent
      ].join('|');

      const mergedItem = mergedItemsBySplitKey[splitKey];

      if (mergedItem) {
        mergedItem.quantity += groupedItem.quantity;
        mergedItem.convertedPrice += groupedItem.convertedPrice;
        mergedItem.items = [...mergedItem.items, ...(groupedItem.items || [])];
      } else {
        const newItem = {
          ...groupedItem,
          items: [...(groupedItem.items || [])]
        };

        mergedItemsBySplitKey[splitKey] = newItem;
        acc.push(newItem);
      }

      return acc;
    }, []);
  }, [items, viewMode, groupBy, enableGrouping]);

  const preparedItems = useMemo(
    () =>
      map(mergedItems, (groupedItem) => ({
        ...groupedItem,
        id: groupedItem.itemId,
        groupedId: groupedItem.items?.map((item) => item.id)?.join()
      })),
    [mergedItems]
  );

  const flatItems = useMemo(
    () =>
      flatMap(
        map(items, (groupedItem) =>
          map(groupedItem.items, (item) => ({
            ...groupedItem,
            ...item,
            itemId: item.id,
            groupedId: item.id
          }))
        )
      ),
    [items]
  );

  return {
    preparedItems,
    flatItems
  };
}

export default useInvoiceItemsViewPreparedItems;
